import React, { useState, useEffect } from "react";
import FadeUp from "@frontend/app/anims/FadeUp.tsx";
import { useMutation } from "@tanstack/react-query";
import { graphql } from "@gql/gql";
import { executeGraphQL } from "@frontend/app/api/graphqlClient.ts";

const sendMessageMutation = graphql(`
  mutation sendMessage($input: MessageInput!) {
    createMessage(input: $input) {
      _id
    }
  }
`);

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "">("");

  const mutation = useMutation({
    mutationFn: async (variables: { input: { name: string; email: string; message: string } }) => {
      return executeGraphQL(sendMessageMutation, variables);
    },
  });

  const { isPending } = mutation;

  useEffect(() => {
    if (alertMessage) {
      const timer = setTimeout(() => {
        setAlertMessage("");
        setAlertType("");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [alertMessage]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    mutation.mutate(
      {
        input: {
          name: formData.name,
          email: formData.email,
          message: formData.message,
        },
      },
      {
        onSuccess: () => {
          setAlertMessage("Message sent successfully!");
          setAlertType("success");
          setFormData({
            name: "",
            email: "",
            message: "",
          });
        },
        onError: (error: any) => {
          setAlertMessage(`Error sending message: ${error?.message || "Unknown error"}`);
          setAlertType("error");
        },
      }
    );
  };

  return (
    <div className="my-16  w-fit m-auto">
      <FadeUp>
        <h1 className="glass p-4 text-5xl glass-blur-in">Get in touch</h1>
        <form onSubmit={handleSubmit} className="grid grid-flow-row gap-y-3 glass my-10 p-6">
          <span className="grid md:gap-3 grid-cols-1 md:grid-cols-[min-content_auto] text-left">
            <label htmlFor="name" className="text-nowrap p-2.5">
              Name:
            </label>
            <input
              required
              type="text"
              name="name"
              id="name"
              value={formData.name}
              onChange={handleChange}
              className="p-2.5 bg-grey-80 rounded-lg"
            />

            <label htmlFor="email" className="p-2.5">
              Email:
            </label>
            <input
              required
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              className="p-2.5 bg-grey-80 rounded-lg"
            />

            <label htmlFor="message" className="p-2.5">
              Message:
            </label>
            <textarea
              required
              name="message"
              id="message"
              value={formData.message}
              onChange={handleChange}
              cols={60}
              className="p-2.5 bg-grey-80 rounded-lg h-32 md:h-64"
            />
          </span>

          <button
            type="submit"
            disabled={isPending}
            className="bg-slate-700 hover:border-none hover:bg-slate-600 transition-bg p-2.5 rounded-lg"
          >
            {isPending ? "Sending..." : "Send"}
          </button>

          {alertMessage && (
            <div
              className={`mt-2 p-2 rounded-lg ${
                alertType === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
              }`}
            >
              {alertMessage}
            </div>
          )}
        </form>
      </FadeUp>
    </div>
  );
};

export default ContactPage;
